/**
 * Funciones utilitarias para mapeo y transformación de datos de evaluaciones
 */

// Mapeo alineado con BD para el campo Evaluacion.IdSubtipoPruebaPsicologica.
const SUBTIPO_PSICOLOGICO_ID_MAP = {
  dass21: 1,
  salamanca: 2
};

const normalizarSubtipo = (valor = '') =>
  {
    const normalizado = valor
      .toString()
      .trim()
      .toLowerCase();

    if (['dass21', 'dass-21', 'personalidad'].includes(normalizado)) {
      return 'dass21';
    }

    return normalizado === 'salamanca' ? 'salamanca' : '';
  };

export const getIdSubtipoPsicologico = (subtipo) => {
  const clave = normalizarSubtipo(subtipo);
  return SUBTIPO_PSICOLOGICO_ID_MAP[clave] || null;
};

export const getSubtipoPsicologicoById = (idSubtipo) => {
  if (idSubtipo === 1) return 'dass21';
  if (idSubtipo === 2) return 'salamanca';
  return '';
};

/**
 * Limpia strings que pueden venir con comillas dobles del backend
 */
export const limpiarString = (valor) => {
  if (typeof valor === 'string') {
    return valor.replace(/^["']|["']$/g, ''); // Remover comillas al inicio y final
  }
  return valor || '';
};

/**
 * Limpia y valida números con límites de rango
 */
export const limpiarNumero = (valor, nombre = 'campo', limite = 999999, minimo = 0) => {
  if (typeof valor === 'string') {
    const limpio = valor.replace(/^["']|["']$/g, ''); // Remover comillas
    let numero = parseFloat(limpio) || 0;
    
    // Validar rango para evitar overflow y pérdida de precisión
    if (numero > limite) {
      console.warn(`⚠️ Valor de ${nombre} (${numero}) excede el límite (${limite}). Ajustando a ${limite}.`);
      numero = limite;
    }
    if (numero < minimo) {
      console.warn(`⚠️ Valor de ${nombre} (${numero}) es menor al mínimo (${minimo}). Ajustando a ${minimo}.`);
      numero = minimo;
    }
    
    return Math.floor(numero); // Asegurar que sea entero
  }
  let numero = parseFloat(valor) || 0;
  if (numero > limite) numero = limite;
  if (numero < minimo) numero = minimo;
  return Math.floor(numero);
};

/**
 * Obtiene el número original de un valor (string o number) para comparaciones
 */
export const obtenerNumeroOriginal = (valor) => {
  if (typeof valor === 'string') {
    return parseFloat(valor.replace(/^["']|["']$/g, '')) || 0;
  }
  return parseFloat(valor) || 0;
};

/**
 * Mapea datos de la API a formato del formulario
 */
export const mapearEvaluacionDesdeAPI = (evaluacionApi, getTipoEvaluacionById, getTipoCargoById) => {
  if (!evaluacionApi) return null;

  console.log('🔍 Analizando campos específicos para mapeo:');
  console.log('  - encabezadoDescripcion:', evaluacionApi.encabezadoDescripcion);
  console.log('  - encabezado:', evaluacionApi.encabezado);
  console.log('  - descripcion:', evaluacionApi.descripcion);
  console.log('  - descripcionCorta:', evaluacionApi.descripcionCorta);
  console.log('  - cargosSeleccionados:', evaluacionApi.cargosSeleccionados);
  console.log('  - idsCargos:', evaluacionApi.idsCargos);
  console.log('  - cargos:', evaluacionApi.cargos);
  console.log('  - listaCargos:', evaluacionApi.listaCargos);
  console.log('  - cargoIds:', evaluacionApi.cargoIds);

  const tipoEvaluacionEncontrado = getTipoEvaluacionById(evaluacionApi.idTipoEvaluacion || evaluacionApi.idTipoEval);
  const tipoCargoEncontrado = getTipoCargoById(evaluacionApi.idTipoCargo);
  const subtipoDesdeApi =
    evaluacionApi.subtipoPruebaPsicologica ||
    evaluacionApi.codigoSubtipoPruebaPsicologica ||
    getSubtipoPsicologicoById(evaluacionApi.idSubtipoPruebaPsicologica);

  return {
    nombreEvaluacion: evaluacionApi.nombreEvaluacion || '',
    tipoPrueba: tipoEvaluacionEncontrado?.value || evaluacionApi.idTipoEvaluacion || evaluacionApi.idTipoEval || '',
    subtipoPruebaPsicologica: normalizarSubtipo(subtipoDesdeApi),
    idTipoCargo: tipoCargoEncontrado?.value || evaluacionApi.idTipoCargo || '',
    tiempoLimite: evaluacionApi.tiempoLimite || evaluacionApi.tiempoLimiteMinutos || 60,
    puntajeTotal: evaluacionApi.puntajeMaximo || 0,
    puntajeAprobatorio: 0,
    pesoCalificacion: evaluacionApi.pesoCalificacion || 0,
    esObligatoria: evaluacionApi.esObligatoria !== undefined ? evaluacionApi.esObligatoria : false,
    estadoActivo: evaluacionApi.estado !== undefined ? evaluacionApi.estado : true,
    // Probar múltiples nombres posibles para encabezadoDescripcion
    encabezadoDescripcion: evaluacionApi.encabezado || 
                          evaluacionApi.encabezadoDescripcion ||
                          evaluacionApi.descripcion || 
                          evaluacionApi.descripcionCorta || '',
    instrucciones: evaluacionApi.instrucciones || '',
    // Probar múltiples nombres posibles para cargosSeleccionados  
    cargosSeleccionados: evaluacionApi.cargosSeleccionados || 
                        evaluacionApi.idsCargos || 
                        evaluacionApi.cargos ||
                        evaluacionApi.listaCargos ||
                        evaluacionApi.cargoIds || [],
    preguntas: mapearPreguntas(evaluacionApi.preguntas || [])
  };
};

/**
 * Mapea datos del formulario para envío al backend
 */
export const mapearEvaluacionParaEnvio = (formData, id, isEditMode) => {
  const etiquetaTipo = formData.tipoPruebaLabel || '';
  const esPsicologica = etiquetaTipo.toLowerCase().includes('psicolog') || String(formData.tipoPrueba) === '248801';
  const idSubtipoPruebaPsicologica = esPsicologica
    ? getIdSubtipoPsicologico(formData.subtipoPruebaPsicologica)
    : null;

  if (isEditMode) {
    return {
      idEvaluacion: parseInt(id),
      nombreEvaluacion: limpiarString(formData.nombreEvaluacion).trim(),
      tiempoLimite: limpiarNumero(formData.tiempoLimite, 'tiempoLimite', 43200), // Máx 30 días
      puntajeMaximo: formData.puntajeTotal || 0,
      puntajeAprobacion: 0,
      pesoCalificacion: limpiarNumero(formData.pesoCalificacion, 'pesoCalificacion', 100, 1) || 1.0, // Entre 1 y 100%
      esObligatoria: Boolean(formData.esObligatoria),
      estado: Boolean(formData.estadoActivo),
      encabezado: limpiarString(formData.encabezadoDescripcion),
      instrucciones: limpiarString(formData.instrucciones),
      idTipoCargo: parseInt(formData.idTipoCargo),
      idTipoEvaluacion: parseInt(formData.tipoPrueba),
      idSubtipoPruebaPsicologica,
      pesoCalificacion: formData.pesoCalificacion === '' || formData.pesoCalificacion === null ? null : parseFloat(formData.pesoCalificacion),
      usuario: 'usuario' // Cambiar de 'sistema' a 'usuario' como en el ejemplo exitoso
    };
  } else {
    return {
      nombreEvaluacion: formData.nombreEvaluacion.trim(),
      tiempoLimite: parseInt(formData.tiempoLimite) || 0,
      puntajeMaximo: formData.puntajeTotal || 0,
      puntajeAprobacion: 0,
      pesoCalificacion: parseFloat(formData.pesoCalificacion) || 1.0,
      esObligatoria: Boolean(formData.esObligatoria),
      estado: Boolean(formData.estadoActivo),
      encabezado: formData.encabezadoDescripcion || '',
      instrucciones: formData.instrucciones || '',
      idTipoCargo: parseInt(formData.idTipoCargo),
      idTipoEvaluacion: parseInt(formData.tipoPrueba),
      idSubtipoPruebaPsicologica,
      cargosSeleccionados: formData.cargosSeleccionados || [],
      pesoCalificacion: formData.pesoCalificacion === '' || !formData.pesoCalificacion ? null : parseFloat(formData.pesoCalificacion),
    };
  }
};

/**
 * Mapea preguntas de la API
 */
export const mapearPreguntas = (preguntasApi) => {
  if (!Array.isArray(preguntasApi)) {
    console.log('⚠️ No se encontraron preguntas en la API');
    return [];
  }
  
  console.log('🔄 Mapeando', preguntasApi.length, 'preguntas desde API');
  
  return preguntasApi.map((pregunta, index) => {
    const preguntaMapeada = {
      id: pregunta.idEvaluacionPregunta || `pregunta_${index + 1}`,
      pregunta: pregunta.textoPregunta || '',
      numero: pregunta.numero || (index + 1),
      esObligatoria: pregunta.esObligatoria !== undefined ? pregunta.esObligatoria : true,
      puntaje: 0, // Se calculará desde las opciones
      opciones: mapearOpciones(pregunta.opciones || [])
    };
    
    // Calcular puntaje de la pregunta basado en opciones
    if (preguntaMapeada.opciones.length > 0) {
      preguntaMapeada.puntaje = Math.max(...preguntaMapeada.opciones.map(o => o.puntaje || 0));
    }
    
    return preguntaMapeada;
  });
};

/**
 * Mapea opciones de la API
 */
export const mapearOpciones = (opcionesApi) => {
  if (!Array.isArray(opcionesApi)) {
    return [];
  }
  
  return opcionesApi.map((opcion, index) => {
    const opcionMapeada = {
      id: opcion.idOpcionRespuesta || opcion.idOpcion || `opcion_${index + 1}`,
      texto: opcion.textoOpcion || opcion.texto || '',
      esCorrecta: opcion.esCorrecta !== undefined ? opcion.esCorrecta : false,
      puntaje: opcion.puntaje || 0,
      orden: opcion.orden || (index + 1)
    };
    
    // Log para debug si los campos del backend son diferentes
    if ((opcion.idOpcionRespuesta && !opcion.idOpcion) || (opcion.textoOpcion && !opcion.texto)) {
      console.log('📝 Opción mapeada desde API:', {
        backend: { 
          idOpcionRespuesta: opcion.idOpcionRespuesta, 
          textoOpcion: opcion.textoOpcion,
          puntaje: opcion.puntaje,
          esCorrecta: opcion.esCorrecta 
        },
        frontend: { 
          id: opcionMapeada.id, 
          texto: opcionMapeada.texto,
          puntaje: opcionMapeada.puntaje,
          esCorrecta: opcionMapeada.esCorrecta
        }
      });
    }
    
    return opcionMapeada;
  });
};